import React, { useEffect, useState } from "react";
import {
  Card,
  Row,
  Col,
  Typography,
  Button,
  Input,
  Select,
  Table,
  Space,
  Tooltip,
  message,
  Popconfirm,
  Modal,
} from "antd";
import {
  PlusOutlined,
  FileExcelOutlined,
  SearchOutlined,
  ReloadOutlined,
  EditOutlined,
  DeleteOutlined,
  PrinterOutlined
} from "@ant-design/icons";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import axiosInstance, { baseURL } from "../../../configs"; // sesuaikan path

import { canCreate, canEdit, canDelete, MODULES } from "../../../utils/rbac";

const { Title, Text } = Typography;
const { Option } = Select;

const defaultPageSize = 20;

export default function BrowsePemusnahan(props) {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: defaultPageSize,
    total: 0,
  });

  const [printUrl, setPrintUrl] = useState(null);
  const [showPrintModal, setShowPrintModal] = useState(false);

  // filter
  const [searchName, setSearchName] = useState("");
  const [searchCode, setSearchCode] = useState("");

  const [deletingId, setDeletingId] = useState(null);

  const fetchItems = async (page = 1, pageSize = defaultPageSize) => {
    setLoading(true);
    try {
      const res = await axiosInstance.get("/pemusnahan", {
        params: {
          page,
          limit: pageSize,
        },
      });

      const raw = res.data || {};
      const payload = raw.data || raw;
      const rows =
        payload.items ||
        payload.rows ||
        payload.data ||
        (Array.isArray(payload) ? payload : []);

      const total =
        payload.total ||
        payload.count ||
        payload.pagination?.total ||
        rows.length;

      setItems(rows);
      setPagination({
        current: page,
        pageSize,
        total: total || 0,
      });
    } catch (err) {
      console.error(err);
      message.error("Gagal memuat daftar pemusnahan");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems(1, defaultPageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTableChange = (pager) => {
    fetchItems(pager.current, pager.pageSize);
  };

  const handleExportExcel = () => {
    window.open(`${baseURL}/pemusnahan/export`, "_blank");
  };

  const handleDelete = async (record) => {
    setDeletingId(record.id_pemusnahan);
    try {
      await axiosInstance.delete(`/pemusnahan/${record.id_pemusnahan}`);
      message.success("Pemusnahan berhasil dihapus");
      // reload current page
      fetchItems(pagination.current, pagination.pageSize);
    } catch (err) {
      console.error(err);
      message.error("Gagal menghapus Pemusnahan");
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (value) =>
    value ? dayjs(value).format("DD-MM-YYYY") : "-";

  const handlePrint = (id) => {
    setPrintUrl(`/#/print/pemusnahan/${id}`);
    setShowPrintModal(true);
  };

  const columns = [
    {
      title: "NO",
      dataIndex: "index",
      width: 70,
      align: "center",
      render: (text, record, index) =>
        (pagination.current - 1) * pagination.pageSize + index + 1,
    },
    {
      title: "NO PEMUSNAHAN",
      dataIndex: "no_pemusnahan",
      width: 150,
    },
    {
      title: "TANGGAL PEMUSNAHAN",
      dataIndex: "tanggal_pemusnahan",
      width: 140,
      render: (value) => formatDate(value),
    },
    {
      title: "ALASAN PEMUSNAHAN",
      dataIndex: "catatan",
    },
    {
      title: "AKSI",
      key: "aksi",
      width: 120,
      align: "center",
      fixed: "right",
      render: (_, record) => (
        <Space>
          {canEdit(MODULES.INVENTORY) && (
            <Tooltip title="Edit">
              <Button
                size="small"
                type="primary"
                icon={<EditOutlined />}
                onClick={() => props.history.push(`/inventory/updatepemusnahan/${record.id_pemusnahan}`)}
              />
            </Tooltip>
          )}
          <Tooltip title="Print">
            <Button
              size="small"
              icon={<PrinterOutlined />}
              onClick={() => handlePrint(record.id_pemusnahan)}
            />
          </Tooltip>
          {canDelete(MODULES.INVENTORY) && (
            <Popconfirm
              title="Yakin hapus?"
              onConfirm={() => handleDelete(record)}
            >
              <Button size="small" type="primary" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          )}
        </Space>
      )
    }
  ];

  return (
    <Card className="kt-portlet" bodyStyle={{ padding: 24 }}>
      {/* HEADER TITLE + ACTIONS */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={3} style={{ marginBottom: 4 }}>
            Browse Pemusnahan
          </Title>
          <Text type="secondary">
            Kelola daftar pemusnahan untuk kebutuhan inventory & kepabeanan.
          </Text>
        </Col>
        <Col>
          <Space>
            {canCreate(MODULES.INVENTORY) && (
              <Link to="/inventory/addpemusnahan">
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  style={{
                    background: "#1BC5BD",
                    borderColor: "#1BC5BD",
                    fontWeight: 600,
                  }}
                >
                  Tambah
                </Button>
              </Link>
            )}
            <Button
              type="primary"
              icon={<FileExcelOutlined />}
              onClick={handleExportExcel}
              style={{
                background: "#1E1EFA",
                borderColor: "#1E1EFA",
                fontWeight: 600,
              }}
            >
              Excel
            </Button>
          </Space>
        </Col>
      </Row>

      <Table
        style={{ marginTop: 36 }}
        rowKey="id_pemusnahan"
        dataSource={items}
        columns={columns}
        loading={loading}
        scroll={{ x: 'max-content' }}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showTotal: (total, range) =>
            `Total ${total} data | ${range[0]}-${range[1]}`,
        }}
        onChange={handleTableChange}
      />

      <Modal
        title="Cetak Pemusnahan"
        open={showPrintModal}
        onCancel={() => setShowPrintModal(false)}
        width={1000}
        footer={null}
        style={{ top: 20 }}
      >
        <iframe
          src={printUrl}
          style={{ width: "100%", height: "80vh", border: "none" }}
          title="Print Preview"
        />
      </Modal>
    </Card>
  );
}
